import { useEffect, useState } from "react";
import {
  deleteInvoiceApprovalMatrixRow,
  fetchInvoiceApprovalMatrix,
  fetchInvoicePOConditions,
  importInvoiceApprovalMatrix,
  saveInvoiceApprovalMatrixRow,
} from "../api";

import "./css/InvoiceApprovalMatrixPOView.css";

const MATCH_MODES = ["INCLUDE", "EXCLUDE", "ANY"];

const APPROVER_OPTIONS = [
  { value: "", label: "--Select--" },
  { value: "nasim@gmail.com", label: "Nasim" },
  { value: "chandan@gmail.com", label: "Chandan" },
  { value: "nitin@gmail.com", label: "Nitin" },
  { value: "gaurav@gmail.com", label: "Gaurav" },
  { value: "mayank@gmail.com", label: "Mayank" },
];
  
const APPROVER_FIELDS = ["approver1", "approver2", "approver3", "approver4", "approver5", "approver6"];

function createDraftRow(subconditions) {
  return {
    conditions: Object.fromEntries(
      subconditions.map((condition) => [
        condition.key,
        { mode: "INCLUDE", values: [] },
      ]),
    ),
    approver1: "",
    approver2: "",
    approver3: "",
    approver4: "",
    approver5: "",
    approver6: "",
  };
}

function formatConditionCell(cell, conditionLabel = "") {
  if (!cell) {
    return "-";
  }

  const isAmount =
    String(conditionLabel).trim().toLowerCase() === "amount";

  // --------------------------------
  // Amount
  // --------------------------------
  if (isAmount) {
    const amountValues = cell.values;

    // New format:
    // values: {
    //   lowerLimit: "0",
    //   upperLimit: "500000"
    // }
    if (
      amountValues &&
      typeof amountValues === "object" &&
      !Array.isArray(amountValues)
    ) {
      const lower = amountValues.lowerLimit ?? "";
      const upper = amountValues.upperLimit ?? "";

      if (lower !== "" || upper !== "") {
        return `${lower !== "" ? lower : "0"}-${upper !== "" ? upper : "∞"}`;
      }
    }

    return "-";
  }

  // --------------------------------
  // Normal / Budget conditions
  // --------------------------------
  const modeMap = {
    INCLUDE: "IN",
    EXCLUDE: "EX",
    ANY: "A",
  };

  const mode = modeMap[cell.mode] || cell.mode || "A";

  if (mode === "A") {
    return "A";
  }

  const values = Array.isArray(cell.values)
    ? cell.values
    : cell.value != null && cell.value !== ""
      ? [cell.value]
      : [];

  return `${mode}${values.length ? `: ${values.join(", ")}` : ""}`;
}

function poSubconditionFromApi(condition) {
  return {
    key: condition.conditionKey,
    label: condition.subConditionName || condition.conditionKey,
    values: Array.isArray(condition.availableValues) ? condition.availableValues : [],
  };
}

export default function InvoiceApprovalMatrixPOView() {
  const [rows, setRows] = useState([]);
  const [subconditions, setSubconditions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [conditionsLoading, setConditionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [draft, setDraft] = useState(createDraftRow([]));

  async function loadRows() {
    setLoading(true);
    setErrorMessage("");
    try {
      const data = await fetchInvoiceApprovalMatrix("po");
      setRows(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setErrorMessage(requestError.message || "Unable to load invoice approval matrix.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, []);

  useEffect(() => {
    let isActive = true;
    setConditionsLoading(true);

    fetchInvoicePOConditions()
      .then((data) => {
        if (!isActive) return;
        const conditions = Array.isArray(data) ? data : data?.conditions || [];
        const nextSubconditions = conditions
          .slice()
          .sort((left, right) => left.displayOrder - right.displayOrder)
          .map(poSubconditionFromApi);
        setSubconditions(nextSubconditions);
        setDraft(createDraftRow(nextSubconditions));
      })
      .catch((requestError) => {
        if (isActive) {
          setErrorMessage(requestError.message || "Unable to load configured PO conditions.");
        }
      })
      .finally(() => {
        if (isActive) {
          setConditionsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  function updateDraftCondition(conditionKey, field, value) {
    setDraft((current) => ({
      ...current,
      conditions: {
        ...current.conditions,
        [conditionKey]: {
          ...current.conditions[conditionKey],
          [field]: value,
        },
      },
    }));
  }

  function updateDraftApprover(field, value) {
    setDraft((current) => ({
      ...current,
      [field]: value,
    }));
  }

  async function handleSubmit() {
    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      const cleanedConditions = Object.fromEntries(
        Object.entries(draft.conditions).map(([key, condition]) => {
          const subcondition = subconditions.find(
            (item) => item.key === key
          );

          // -------------------------
          // Amount
          // -------------------------
          if (subcondition?.label === "Amount") {
            return [
              key,
              {
                mode: "INCLUDE",
                values: {
                  lowerLimit: condition.values?.lowerLimit ?? "",
                  upperLimit: condition.values?.upperLimit ?? "",
                },
              },
            ];
          }

          // -------------------------
          // Budget
          // -------------------------
          if (subcondition?.label === "Budget") {
            return [
              key,
              {
                mode: "INCLUDE",
                values: condition.values || [],
              },
            ];
          }

          // -------------------------
          // Normal conditions
          // -------------------------
          return [key, condition];
        })
      );

      const payload = {
        matrix_type: "po",
        ...draft,
        conditions: cleanedConditions,
      };

      console.log("APPROVAL MATRIX PAYLOAD:", payload);

      const response = await saveInvoiceApprovalMatrixRow(payload);

      setRows(
        Array.isArray(response?.data)
          ? response.data
          : [...rows, response.row]
      );

      setDraft(createDraftRow(subconditions));

      setStatusMessage(
        response?.message ||
          "Invoice approval matrix row saved successfully."
      );
    } catch (requestError) {
      setErrorMessage(
        requestError.message ||
          "Unable to save invoice approval matrix row."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(rowId) {
    setStatusMessage("");
    setErrorMessage("");
    try {
      const response = await deleteInvoiceApprovalMatrixRow(rowId);
      setRows(Array.isArray(response?.data) ? response.data : rows.filter((row) => row.id !== rowId));
      setStatusMessage(response?.message || "Invoice approval matrix row deleted successfully.");
    } catch (requestError) {
      setErrorMessage(requestError.message || "Unable to delete invoice approval matrix row.");
    }
  }

  async function handleUpload(event) {
    const [file] = event.target.files || [];
    if (!file) {
      return;
    }

    setSaving(true);
    setStatusMessage("");
    setErrorMessage("");
    try {
      const response = await importInvoiceApprovalMatrix("po", file);
      setRows(Array.isArray(response?.data) ? response.data : rows);
      setStatusMessage(response?.message || "Path table imported successfully.");
    } catch (requestError) {
      setErrorMessage(requestError.message || "Unable to import path table.");
    } finally {
      setSaving(false);
      event.target.value = "";
    }
  }

  return (
    <section className="conditions-view conditions-view--approval-matrix">
      <div className="workspace-panel conditions-view__hero">
        <div>
          <h2>Approval Matrix_PO</h2>
          <p className="config-view__intro">PO invoice approval path setup.</p>
        </div>
      </div>

      <div className="workspace-panel conditions-panel">
        <div className="users-form__header">
          <div>
            <p className="eyebrow">PO Invoices</p>
            <h2>Path Table</h2>
          </div>
        </div>

        {statusMessage ? <div className="config-banner config-banner--success">{statusMessage}</div> : null}
        {errorMessage ? <div className="config-banner config-banner--error">{errorMessage}</div> : null}
        {!conditionsLoading && !subconditions.length ? (
          <div className="config-note">
            Add PO conditions in Conditions_PO before configuring the approval path table.
          </div>
        ) : null}

        <div className="config-table conditions-table-wrap">
          <table className="approval-matrix-table">
            <thead>
              <tr>
                <th>id</th>
                {subconditions.map((condition) => (
                  <th key={condition.key}>{condition.label}</th>
                ))}
                {APPROVER_FIELDS.map((field, index) => (
                  <th key={field}>{`approver${index + 1}`}</th>
                ))}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading || conditionsLoading ? (
                <tr className="approval-matrix-input-row">
                  <td colSpan={subconditions.length + APPROVER_FIELDS.length + 2}>
                    Loading path table...
                  </td>
                </tr>
              ) : !rows.length ? (
                <tr>
                  <td colSpan={subconditions.length + APPROVER_FIELDS.length + 2}>
                    No data available for this company, Please Upload Path Table using below Button or insert a row using below input.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    {subconditions.map((condition) => (
                      <td key={condition.key}>
                        {formatConditionCell(
                          row.conditions[condition.key],
                          condition.label
                        )}
                      </td>
                    ))}
                    {APPROVER_FIELDS.map((field) => (
                      <td key={field}>{row[field] || "-"}</td>
                    ))}
                    <td>
                      <button
                        className="condition-icon-button condition-icon-button--danger"
                        type="button"
                        onClick={() => handleRemove(row.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}

              <tr>
                <td>{rows.length + 1}</td>
                {subconditions.map((condition) => {
                  const cell = draft.conditions[condition.key] || {
                    mode: "INCLUDE",
                    values: [],
                  };

                  const isBudget = condition.label === "Budget";
                  const isAmount = condition.label === "Amount";

                  // -------------------------
                  // Budget
                  // -------------------------
                  if (isBudget) {
                    const budgetValue = (cell.values || []).join(",");

                    return (
                      <td key={condition.key}>
                        <select
                          className="approval-matrix-budget-select"
                          value={budgetValue}
                          onChange={(event) => {
                            const value = event.target.value;

                            updateDraftCondition(
                              condition.key,
                              "mode",
                              "INCLUDE"
                            );

                            updateDraftCondition(
                              condition.key,
                              "values",
                              value ? value.split(",") : []
                            );
                          }}
                        >
                          <option value="">ANY</option>
                          <option value="Y">Y</option>
                          <option value="N">N</option>
                          <option value="Y,N">Y, N</option>
                        </select>
                      </td>
                    );
                  }

                  // -------------------------
                  // Amount
                  // -------------------------
                  if (isAmount) {
                    const amountValues =
                      cell.values && !Array.isArray(cell.values)
                        ? cell.values
                        : {
                            lowerLimit: "",
                            upperLimit: "",
                          };

                    return (
                      <td key={condition.key}>
                        <div className="approval-matrix-amount">
                          <input
                            className="approval-matrix-amount__input"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Lower"
                            value={amountValues.lowerLimit ?? ""}
                            onChange={(event) =>
                              updateDraftCondition(
                                condition.key,
                                "values",
                                {
                                  ...amountValues,
                                  lowerLimit: event.target.value,
                                }
                              )
                            }
                          />

                          <span className="approval-matrix-amount__separator">
                            –
                          </span>

                          <input
                            className="approval-matrix-amount__input"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Upper"
                            value={amountValues.upperLimit ?? ""}
                            onChange={(event) =>
                              updateDraftCondition(
                                condition.key,
                                "values",
                                {
                                  ...amountValues,
                                  upperLimit: event.target.value,
                                }
                              )
                            }
                          />
                        </div>
                      </td>
                    );
                  }

                  // -------------------------
                  // Normal conditions
                  // -------------------------
                  return (
                    <td key={condition.key}>
                      <div className="condition-cell-stack">
                        <select
                          className="approval-matrix-condition-select"
                          value={cell.mode}
                          onChange={(event) =>
                            updateDraftCondition(
                              condition.key,
                              "mode",
                              event.target.value
                            )
                          }
                        >
                          {MATCH_MODES.map((mode) => (
                            <option key={mode} value={mode}>
                              {mode}
                            </option>
                          ))}
                        </select>

                        <details
                          className={`approval-matrix-values${
                            cell.mode === "ANY" || !condition.values.length
                              ? " is-disabled"
                              : ""
                          }`}
                        >
                          <summary
                            onClick={(event) => {
                              if (
                                cell.mode === "ANY" ||
                                !condition.values.length
                              ) {
                                event.preventDefault();
                              }
                            }}
                          >
                            {(cell.values || []).length
                              ? (cell.values || []).join(", ")
                              : condition.values.length
                                ? "--Select--"
                                : "No values configured"}
                          </summary>

                          {condition.values.length ? (
                            <div className="approval-matrix-values__menu">
                              {condition.values.map((value) => {
                                const isChecked = (cell.values || []).includes(value);

                                return (
                                  <label key={value}>
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      disabled={cell.mode === "ANY"}
                                      onChange={() =>
                                        updateDraftCondition(
                                          condition.key,
                                          "values",
                                          isChecked
                                            ? (cell.values || []).filter(
                                                (item) => item !== value
                                              )
                                            : [
                                                ...(cell.values || []),
                                                value,
                                              ]
                                        )
                                      }
                                    />

                                    <span>{value}</span>
                                  </label>
                                );
                              })}
                            </div>
                          ) : null}
                        </details>
                      </div>
                    </td>
                  );
                })}
                {APPROVER_FIELDS.map((field) => (
                  <td key={field}>
                    <select value={draft[field]} onChange={(event) => updateDraftApprover(field, event.target.value)}>
                      {APPROVER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                ))}
                <td>
                  <button
                    className="primary-button"
                    type="button"
                    onClick={handleSubmit}
                    disabled={saving || !subconditions.length}
                  >
                    {saving ? "Saving..." : "Submit"}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="condition-actions-bar">
          <label className="ghost-button" htmlFor="invoice-path-upload-po">
            Upload Path Table
          </label>
          <input
            id="invoice-path-upload-po"
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleUpload}
            hidden
          />
        </div>
      </div>
    </section>
  );
}
